import React from "react";
import Prop from "../Prop";
import ParticleField from "../ParticleField";
import { Palette } from "../holidayConfig";
import { cubic, range, seeded } from "./sceneUtils";

/*
 * Midsommar: äng, midsommarstång, björklöv, prästkragar, blåklint,
 * smörblommor, jordgubbar och små grodor som hoppar.
 */

/** Blommor som <g> så att de kan strös ut i landskapet. */
const DaisyG: React.FC<{ x: number; y: number; s?: number }> = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    {range(9).map(i => (
      <ellipse key={i} cx="0" cy="-8" rx="3" ry="7" fill="#ffffff" transform={`rotate(${i * 40})`} />
    ))}
    <circle r="4" fill="#facc15" />
    <circle r="2" fill="#f59e0b" opacity="0.6" />
  </g>
);

const CornflowerG: React.FC<{ x: number; y: number; s?: number }> = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    {range(8).map(i => (
      <path key={i} d="M0 0 L-3.2 -9 L-1 -7 L0 -11 L1 -7 L3.2 -9 Z" fill="#3b82f6" transform={`rotate(${i * 45})`} />
    ))}
    <circle r="2.6" fill="#1e3a8a" />
  </g>
);

const ButtercupG: React.FC<{ x: number; y: number; s?: number }> = ({ x, y, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    {range(5).map(i => (
      <circle key={i} cx="0" cy="-5" r="4.2" fill="#fde047" transform={`rotate(${i * 72})`} />
    ))}
    <circle r="2.4" fill="#f59e0b" />
  </g>
);

const Flower: React.FC<{ kind: "daisy" | "cornflower" | "buttercup" }> = ({ kind }) => (
  <svg viewBox="-20 -20 40 84" xmlns="http://www.w3.org/2000/svg">
    <path d="M0 8 V 60" stroke="#4d7c0f" strokeWidth="2.4" strokeLinecap="round" />
    <path d="M0 40 C -8 34, -14 36, -16 44 C -8 44, -4 42, 0 40 Z" fill="#65a30d" />
    <path d="M0 50 C 8 44, 14 46, 16 54 C 8 54, 4 52, 0 50 Z" fill="#4d7c0f" />
    {kind === "daisy" && <DaisyG x={0} y={0} s={1.5} />}
    {kind === "cornflower" && <CornflowerG x={0} y={0} s={1.4} />}
    {kind === "buttercup" && <ButtercupG x={0} y={0} s={1.5} />}
  </svg>
);

/** Löv på en kvist – en lövruska av björk. */
const LeafG: React.FC<{ x: number; y: number; r: number; s: number; color: string }> = ({ x, y, r, s, color }) => (
  <path
    d="M0 -9 C 6 -7, 8 -1, 0 9 C -8 -1, -6 -7, 0 -9 Z"
    fill={color}
    transform={`translate(${x} ${y}) rotate(${r}) scale(${s})`}
  />
);

const BIRCH_GREENS = ["#86efac", "#4ade80", "#22c55e", "#16a34a"];

const BirchBranch: React.FC = () => {
  const rnd = seeded(21);
  const branches: Array<[[number, number], [number, number], [number, number], [number, number]]> = [
    [[110, 0], [104, 70], [70, 130], [40, 210]],
    [[110, 0], [118, 80], [150, 140], [176, 220]],
    [[108, 40], [96, 80], [82, 100], [66, 118]],
    [[114, 60], [128, 96], [140, 108], [150, 128]],
  ];
  const leaves: Array<{ x: number; y: number; r: number; s: number; c: string }> = [];
  branches.forEach((b, bi) => {
    const count = bi < 2 ? 16 : 8;
    for (let i = 1; i <= count; i++) {
      const t = i / count;
      const [x, y] = cubic(b[0], b[1], b[2], b[3], t);
      const side = i % 2 ? 1 : -1;
      leaves.push({
        x: x + side * (6 + rnd() * 8),
        y: y + rnd() * 6,
        r: side * (30 + rnd() * 50) + 90,
        s: 0.8 + rnd() * 0.7,
        c: BIRCH_GREENS[Math.floor(rnd() * BIRCH_GREENS.length)],
      });
    }
  });
  return (
    <svg viewBox="0 0 220 240" xmlns="http://www.w3.org/2000/svg">
      <g fill="none" stroke="#a8a29e" strokeWidth="3" strokeLinecap="round">
        {branches.map((b, i) => (
          <path key={i} d={`M${b[0][0]} ${b[0][1]} C ${b[1][0]} ${b[1][1]}, ${b[2][0]} ${b[2][1]}, ${b[3][0]} ${b[3][1]}`} strokeWidth={i < 2 ? 3.5 : 2} />
        ))}
      </g>
      {leaves.map((l, i) => (
        <LeafG key={i} x={l.x} y={l.y} r={l.r} s={l.s} color={l.c} />
      ))}
    </svg>
  );
};

/** Midsommarstången med lövklädd stång, tvärslå och två kransar. */
const Maypole: React.FC = () => {
  const poleLeaves = range(20).map(i => ({ y: 150 + i * 22, side: i % 2 ? 1 : -1 }));
  const barLeaves = range(12).map(i => ({ x: 38 + i * 11, side: i % 2 ? 1 : -1 }));
  const ring = (cx: number, cy: number) => (
    <g>
      <circle cx={cx} cy={cy} r="30" fill="none" stroke="#166534" strokeWidth="9" />
      {range(16).map(i => {
        const a = (i / 16) * Math.PI * 2;
        const x = cx + Math.cos(a) * 30;
        const y = cy + Math.sin(a) * 30;
        return <ellipse key={i} cx={x} cy={y} rx="4" ry="7.5" fill={BIRCH_GREENS[i % 4]} transform={`rotate(${(a * 180) / Math.PI + (i % 2 ? 40 : -40)} ${x} ${y})`} />;
      })}
      <DaisyG x={cx} y={cy - 30} s={0.7} />
      <CornflowerG x={cx + 26} y={cy + 15} s={0.7} />
      <DaisyG x={cx - 26} y={cy + 15} s={0.7} />
      <ButtercupG x={cx} y={cy + 30} s={0.7} />
    </g>
  );
  return (
    <svg viewBox="0 0 200 600" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="100" cy="594" rx="50" ry="6" fill="#000" opacity="0.14" />
      {/* Band från toppen, svenska färger */}
      <path d="M100 42 C 132 54, 152 30, 178 48" fill="none" stroke="#2563eb" strokeWidth="4" strokeLinecap="round" />
      <path d="M100 42 C 68 56, 48 32, 22 50" fill="none" stroke="#facc15" strokeWidth="4" strokeLinecap="round" />
      {/* Stång */}
      <rect x="94" y="40" width="12" height="556" rx="3" fill="#a16207" />
      <rect x="97" y="40" width="3" height="556" fill="#fde68a" opacity="0.35" />
      {poleLeaves.map((l, i) => (
        <g key={i}>
          <ellipse cx={100 + l.side * 8} cy={l.y} rx="5" ry="9" fill={BIRCH_GREENS[i % 4]} transform={`rotate(${l.side * 35} ${100 + l.side * 8} ${l.y})`} />
          <ellipse cx={100 - l.side * 7} cy={l.y + 10} rx="4" ry="7" fill={BIRCH_GREENS[(i + 2) % 4]} transform={`rotate(${-l.side * 30} ${100 - l.side * 7} ${l.y + 10})`} />
        </g>
      ))}
      {/* Tvärslå */}
      <rect x="30" y="124" width="140" height="10" rx="3" fill="#a16207" />
      {barLeaves.map((l, i) => (
        <ellipse key={i} cx={l.x} cy={129 + l.side * 6} rx="7" ry="4.5" fill={BIRCH_GREENS[i % 4]} transform={`rotate(${l.side * 25} ${l.x} ${129 + l.side * 6})`} />
      ))}
      {/* Kransar */}
      <path d="M40 134 V 158 M160 134 V 158" stroke="#a16207" strokeWidth="2.5" />
      {ring(40, 190)}
      {ring(160, 190)}
      {/* Topp */}
      <circle cx="100" cy="60" r="20" fill="none" stroke="#166534" strokeWidth="8" />
      {range(10).map(i => {
        const a = (i / 10) * Math.PI * 2;
        const x = 100 + Math.cos(a) * 20;
        const y = 60 + Math.sin(a) * 20;
        return <ellipse key={i} cx={x} cy={y} rx="3.5" ry="6.5" fill={BIRCH_GREENS[i % 4]} transform={`rotate(${(a * 180) / Math.PI + 40} ${x} ${y})`} />;
      })}
      <DaisyG x={100} y={40} s={0.6} />
      <CornflowerG x={118} y={70} s={0.55} />
      <ButtercupG x={82} y={70} s={0.55} />
      {/* Stenar vid foten */}
      <ellipse cx="72" cy="592" rx="12" ry="6" fill="#a8a29e" />
      <ellipse cx="130" cy="593" rx="9" ry="5" fill="#d6d3d1" />
    </svg>
  );
};

const Frog: React.FC = () => (
  <svg viewBox="0 0 80 62" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="40" cy="59" rx="26" ry="3" fill="#000" opacity="0.14" />
    {/* Bakben */}
    <path d="M18 48 C 4 50, 2 58, 14 58 L 26 56" fill="#22c55e" />
    <path d="M62 48 C 76 50, 78 58, 66 58 L 54 56" fill="#22c55e" />
    <ellipse cx="40" cy="42" rx="26" ry="16" fill="#4ade80" />
    <ellipse cx="40" cy="48" rx="16" ry="8" fill="#bbf7d0" />
    {/* Ögon */}
    <circle cx="27" cy="26" r="9" fill="#4ade80" />
    <circle cx="53" cy="26" r="9" fill="#4ade80" />
    <circle cx="27" cy="25" r="5.5" fill="#ffffff" />
    <circle cx="53" cy="25" r="5.5" fill="#ffffff" />
    <circle cx="28" cy="25" r="2.8" fill="#14532d" />
    <circle cx="54" cy="25" r="2.8" fill="#14532d" />
    <circle cx="29" cy="24" r="0.9" fill="#fff" />
    <circle cx="55" cy="24" r="0.9" fill="#fff" />
    {/* Mun och kinder */}
    <path d="M26 40 q14 10 28 0" fill="none" stroke="#14532d" strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="20" cy="40" r="3.5" fill="#f9a8d4" opacity="0.55" />
    <circle cx="60" cy="40" r="3.5" fill="#f9a8d4" opacity="0.55" />
    {/* Framfötter */}
    <path d="M30 54 l-4 4 m4 -4 l0 5 m0 -5 l4 4" stroke="#22c55e" strokeWidth="2.4" strokeLinecap="round" />
    <path d="M50 54 l-4 4 m4 -4 l0 5 m0 -5 l4 4" stroke="#22c55e" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);

const Strawberry: React.FC = () => (
  <svg viewBox="0 0 40 52" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 50 C 4 40, 2 18, 20 12 C 38 18, 36 40, 20 50 Z" fill="#ef4444" />
    <path d="M20 50 C 30 40, 36 30, 33 18 C 36 30, 30 42, 20 50 Z" fill="#dc2626" />
    {[[13, 24], [22, 20], [28, 28], [15, 34], [24, 32], [20, 42], [30, 38]].map(([x, y], i) => (
      <ellipse key={i} cx={x} cy={y} rx="1.2" ry="1.8" fill="#fde68a" />
    ))}
    <path d="M20 14 C 12 8, 6 12, 10 16 Z M20 14 C 28 8, 34 12, 30 16 Z M20 14 C 16 6, 22 4, 24 12 Z" fill="#16a34a" />
    <path d="M20 12 V 3" stroke="#15803d" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const Sun: React.FC = () => (
  <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" style={{ overflow: "visible" }}>
    <g className="hol-sun-rays" style={{ transformOrigin: "100px 100px", animation: "hol-rays 90s linear infinite" }}>
      {range(12).map(i => (
        <path key={i} d="M100 14 L 108 40 L 92 40 Z" fill="#fde047" opacity="0.75" transform={`rotate(${i * 30} 100 100)`} />
      ))}
    </g>
    <circle cx="100" cy="100" r="62" fill="#fef08a" opacity="0.5" />
    <circle cx="100" cy="100" r="50" fill="#fde047" />
    <circle cx="100" cy="100" r="42" fill="#facc15" />
    <circle cx="84" cy="84" r="12" fill="#fef9c3" opacity="0.7" />
  </svg>
);

const Landscape: React.FC<{ p: Palette; dark: boolean }> = ({ p, dark }) => {
  const rnd = seeded(99);
  const flowers = range(70).map(() => {
    const x = rnd() * 2400;
    const kind = rnd();
    return { x, y: 300 + rnd() * 85, kind, s: 0.8 + rnd() * 0.9 };
  });
  const grass = range(60).map(() => ({ x: rnd() * 2400, y: 290 + rnd() * 100, s: 0.7 + rnd() * 1 }));
  return (
    <svg className="hol-landscape" viewBox="0 0 2400 400" preserveAspectRatio="xMidYMax slice" xmlns="http://www.w3.org/2000/svg">
      <path d="M0 230 C 300 170, 700 250, 1100 200 C 1500 150, 1900 240, 2400 180 L2400 400 L0 400 Z" fill={dark ? p[800] : p[200]} opacity={dark ? 0.55 : 1} />
      <path d="M0 290 C 400 240, 800 320, 1200 280 C 1600 240, 2000 320, 2400 270 L2400 400 L0 400 Z" fill={dark ? p[800] : p[300]} opacity={dark ? 0.85 : 1} />
      <path d="M0 340 C 300 310, 700 370, 1100 340 C 1500 310, 1900 375, 2400 330 L2400 400 L0 400 Z" fill={dark ? p[700] : p[400]} opacity={dark ? 0.8 : 0.9} />
      <g stroke={dark ? p[400] : p[600]} strokeOpacity="0.7" strokeWidth="2.6" strokeLinecap="round" fill="none">
        {grass.map((g, i) => (
          <path key={i} d={`M${g.x} ${g.y} l${-5 * g.s} ${-14 * g.s} M${g.x} ${g.y} l${1 * g.s} ${-18 * g.s} M${g.x} ${g.y} l${6 * g.s} ${-13 * g.s}`} />
        ))}
      </g>
      {flowers.map((f, i) =>
        f.kind < 0.45 ? (
          <DaisyG key={i} x={f.x} y={f.y} s={f.s} />
        ) : f.kind < 0.75 ? (
          <CornflowerG key={i} x={f.x} y={f.y} s={f.s} />
        ) : (
          <ButtercupG key={i} x={f.x} y={f.y} s={f.s} />
        )
      )}
    </svg>
  );
};

const MidsummerScene: React.FC<{ p: Palette; dark: boolean }> = ({ p, dark }) => (
  <>
    <ParticleField kind="petals" dark={dark} />

    {/* Sol på dagen, ljus midsommarnatt i darkmode */}
    {dark ? (
      <div
        style={{
          position: "absolute",
          left: "-10vw",
          bottom: "8vh",
          width: "70vw",
          height: "45vh",
          background: `radial-gradient(ellipse at 40% 100%, ${p[400]}55 0%, ${p[500]}22 35%, transparent 70%)`,
        }}
      />
    ) : (
      <Prop depth={0.25} right="7%" top="10vh" width="clamp(90px, 11vw, 200px)" hideBelow="sm">
        <Sun />
      </Prop>
    )}

    <Landscape p={p} dark={dark} />

    {/* Björkruskor som hänger ner från kanten. Hörnet uppe till vänster
        hålls fritt för logotypen. */}
    <Prop depth={0.5} right="24%" top="-3vh" width="clamp(110px, 12vw, 220px)" anim="sway-top" duration="11s" delay="-4s" flip hideBelow="md">
      <BirchBranch />
    </Prop>
    <Prop depth={0.4} left="32%" top="-4vh" width="clamp(100px, 10vw, 180px)" anim="sway-top" duration="10s" delay="-2s" hideBelow="md">
      <BirchBranch />
    </Prop>

    {/* Midsommarstången */}
    <Prop depth={0.85} left="6%" bottom="1.5vh" width="clamp(84px, 8.5vw, 150px)">
      <Maypole />
    </Prop>

    {/* Små grodorna */}
    <Prop left="21%" bottom="1.5vh" width="clamp(36px, 4vw, 64px)" anim="hop" duration="4.2s" hideBelow="md">
      <Frog />
    </Prop>
    <Prop right="10%" bottom="1.2vh" width="clamp(40px, 4.4vw, 70px)" anim="hop" duration="5s" delay="-2.1s">
      <Frog />
    </Prop>
    <Prop right="27%" bottom="0.8vh" width="clamp(32px, 3.6vw, 56px)" anim="hop" duration="4.6s" delay="-3.3s" hideBelow="lg">
      <Frog />
    </Prop>

    {/* Jordgubbar */}
    <Prop left="17%" bottom="0.6vh" width="clamp(20px, 2.2vw, 36px)" rotate={-8} hideBelow="sm">
      <Strawberry />
    </Prop>
    <Prop right="20%" bottom="0.5vh" width="clamp(20px, 2.2vw, 36px)" rotate={10} hideBelow="md">
      <Strawberry />
    </Prop>
    <Prop right="4%" bottom="0.5vh" width="clamp(18px, 2vw, 32px)" rotate={-14}>
      <Strawberry />
    </Prop>

    {/* Stora blommor i förgrunden */}
    <Prop left="1%" bottom="0.5vh" width="clamp(40px, 4.6vw, 78px)" anim="sway" duration="8s">
      <Flower kind="daisy" />
    </Prop>
    <Prop left="14%" bottom="0.2vh" width="clamp(34px, 3.8vw, 64px)" anim="sway" duration="9s" delay="-3s" hideBelow="sm">
      <Flower kind="cornflower" />
    </Prop>
    <Prop left="27%" bottom="0vh" width="clamp(34px, 3.8vw, 64px)" anim="sway" duration="7.5s" delay="-1s" hideBelow="lg">
      <Flower kind="buttercup" />
    </Prop>
    <Prop right="15%" bottom="0.2vh" width="clamp(34px, 3.8vw, 64px)" anim="sway" duration="8.5s" delay="-2s" hideBelow="md">
      <Flower kind="cornflower" />
    </Prop>
    <Prop right="32%" bottom="0vh" width="clamp(34px, 3.8vw, 64px)" anim="sway" duration="8s" delay="-4s" hideBelow="lg">
      <Flower kind="daisy" />
    </Prop>
    <Prop right="1%" bottom="0.5vh" width="clamp(40px, 4.6vw, 78px)" anim="sway" duration="7s" delay="-5s">
      <Flower kind="daisy" />
    </Prop>
    <Prop right="6%" bottom="0.2vh" width="clamp(30px, 3.4vw, 56px)" anim="sway" duration="9s" delay="-2.5s" hideBelow="sm">
      <Flower kind="buttercup" />
    </Prop>
  </>
);

export default MidsummerScene;
